export interface CardProperty {
  key: string;
  label: string;
}

export interface CardConfig {
  title: string;
  properties: CardProperty[];
}

export interface FieldRule {
  hide?: boolean;
  moveTo?: string;
  moveToTop?: string;
  moveAfter?: string | string[];
  moveBefore?: string | string[];
}

export interface ProductRules {
  [fieldKey: string]: FieldRule;
}

const threadColors = () => {
  return {
    colorCount: { moveTo: 'MATERIAL SELECTION DETAILS' },
    threadColorCount: { moveTo: 'MATERIAL SELECTION DETAILS' },
    threadColors: { moveTo: 'MATERIAL SELECTION DETAILS' },
    threadColorsDescriptions: { moveTo: 'MATERIAL SELECTION DETAILS' },
    color1ThreadColor: { moveTo: 'MATERIAL SELECTION DETAILS' },
    threadColor1Description: { moveTo: 'MATERIAL SELECTION DETAILS' },
    color2ThreadColor: { moveTo: 'MATERIAL SELECTION DETAILS' },
    threadColor2Description: { moveTo: 'MATERIAL SELECTION DETAILS' },
    color3ThreadColor: { moveTo: 'MATERIAL SELECTION DETAILS' },
    threadColor3Description: { moveTo: 'MATERIAL SELECTION DETAILS' },
    color4ThreadColor: { moveTo: 'MATERIAL SELECTION DETAILS' },
    threadColor4Description: { moveTo: 'MATERIAL SELECTION DETAILS' },
    color5ThreadColor: { moveTo: 'MATERIAL SELECTION DETAILS' },
    threadColor5Description: { moveTo: 'MATERIAL SELECTION DETAILS' },
    color6ThreadColor: { moveTo: 'MATERIAL SELECTION DETAILS' },
    threadColor6Description: { moveTo: 'MATERIAL SELECTION DETAILS' },
    color7ThreadColor: { moveTo: 'MATERIAL SELECTION DETAILS' },
    threadColor7Description: { moveTo: 'MATERIAL SELECTION DETAILS' },
  };
};

// Keep decal-pack fields in the same cards and order as the art-center details.
export const DECAL_PACK_FIELDS: Record<string, string[]> = {
  'ART DETAILS': [
    'artId', 'artName', 'artWidth', 'artHeight', 'isRasterImage',
    'origFileName', 'hasVector', 'originalDesignId', 'quantities', 'totalQuantity',
  ],
  'PRODUCT DETAILS': [
    'serviceType', 'productType', 'outlineType', 'outlineColor',
    'digitalColorHex', 'digitalColorName', 'media_original',
  ],
};

export const DECAL_PACK_RULES: ProductRules = {
  ...Object.fromEntries(Object.entries(DECAL_PACK_FIELDS).flatMap(([title, fields]) =>
    fields.map(key => [key, { moveTo: title }])
  )),
  designId: { hide: true },
  media: { hide: true },
  mediaColor: { hide: true },
  mediaCode: { hide: true },
  mediaText: { hide: true },
  quoteQty: { hide: true },
};

export const PRODUCT_FIELD_RULES: Record<string, ProductRules> = {
  // 3D Embroidered Patches
  '3DEmbrPatches': {
    ...threadColors,
    patchBackgroundFabricColor: { hide: true }
  },
  '3DEmbrPatchesHA': {
    ...threadColors,
    patchBackgroundFabricColor: { hide: true }
  },
  '3DEmbrPatchesPS': {
    ...threadColors,
    patchBackgroundFabricColor: { hide: true }
  },

  // Embroidered Patches
  'EmbrPatchesHA': {
    ...threadColors,
    patchBackgroundFabricColor: { hide: true },
    patchBorderColorId: { hide: true },
    shape: { moveAfter: 'shapeType' }
  },
  'EmbrPatchesPS': {
    ...threadColors,
    patchBackgroundFabricColor: { hide: true },
    patchBorderColorId: { hide: true },
    shape: { moveAfter: 'shapeType' }
  },
  'EmbrPatchesHL': {
    ...threadColors,
    patchBackgroundFabricColor: { hide: true },
    patchBorderColorId: { hide: true },
    shape: { moveAfter: 'shapeType' }
  },

  // Print Stitch Patches
  'PrintStitchPatchesHA': {
    ...threadColors,
    patchBackgroundFabricColor: { hide: true },
    patchBorderThreadColor: { hide: true },
    shape: { moveAfter: 'shapeType' }
  },
  'PrintStitchPatchesPS': {
    ...threadColors,
    patchBackgroundFabricColor: { hide: true },
    patchBorderThreadColor: { hide: true },
    shape: { moveAfter: 'shapeType' }
  },
  'PrintStitchPatchesHL': {
    ...threadColors,
    patchBackgroundFabricColor: { hide: true },
    patchBorderThreadColor: { hide: true },
    shape: { moveAfter: 'shapeType' }
  },

  // Flexbroidery
  'FlexbroideryHA': {
    shape: { moveAfter: 'shapeType' },
    media: { hide: true }
  },
  'FlexbroideryPS': {
    shape: { moveAfter: 'shapeType' },
    media: { hide: true }
  },

  // FlexStyle Emblems
  'BeveledEmblemsHA': {
    mediaText: { hide: true }
  },
  'BeveledEmblemsPS': {
    mediaText: { hide: true }
  },
  'DomeXEmblemsHA': {
    mediaText: { hide: true }
  },
  'DomeXEmblemsPS': {
    mediaText: { hide: true }
  },
  'FlatDomeXEmblemsHA': {
    mediaText: { hide: true }
  },
  'FlatDomeXEmblemsHL': {
    mediaText: { hide: true }
  },
  'FlatDomeXEmblemsPS': {
    mediaText: { hide: true }
  },
  'DecoXEmblemsHA': {
    mediaText: { hide: true }
  },
  'DecoXEmblemsPS': {
    mediaText: { hide: true }
  },

  // Chenille Patches
  'ChenillePatchesHA': {
    ...threadColors,
    patchBackgroundFabricColor: { hide: true },
    patchBorderThreadColor: { hide: true }
  },
  'ChenillePatchesPS': {
    ...threadColors,
    patchBackgroundFabricColor: { hide: true },
    patchBorderThreadColor: { hide: true }
  },

  // "Leather Patches Heat Applied"
  'LeatherPatchesHA': {
    shape: { moveAfter: 'shapeType' },
    mediaText: { hide: true },
  },

  // Leather Patches Pressure Sensitive/Sticker
  'LeatherPatchesPS': {
    shape: { moveAfter: 'shapeType' },
    mediaText: { hide: true },
  },

  // Faux Leather Patches
  'FauxLeatherPatchesHA': {
    shape: { moveAfter: 'shapeType' },
    mediaText: { hide: true }
  },
  'FauxLeatherPatchesPS': {
    shape: { moveAfter: 'shapeType' },
    mediaText: { hide: true }
  },

  // Faux Suede Patches
  'FauxSuedePatchesHA': {
    shape: { moveAfter: 'shapeType' },
  },
  'FauxSuedePatchesPS': {
    shape: { moveAfter: 'shapeType' },
  },

  // FlexStyle Holographic Emblems
  'FlexStyleHolographicHA': {
    shape: { moveAfter: 'shapeType' },
    media: { hide: true },
    mediaText: { hide: true }
  },
  'FlexStyleHolographicPS': {
    shape: { moveAfter: 'shapeType' },
    media: { hide: true },
    mediaText: { hide: true }
  },

  // PVC Patch
  'PVPlusPatchHA': {
    shape: { moveAfter: 'shapeType' },
    numberOfColors: { moveToTop: 'MATERIAL SELECTION DETAILS' }
  },
  'PVPlusPatchPS': {
    shape: { moveAfter: 'shapeType' },
    numberOfColors: { moveToTop: 'MATERIAL SELECTION DETAILS' }
  },
  'DomePVPlusPatchHA': {
    shape: { moveAfter: 'shapeType' },
    numberOfColors: { moveToTop: 'MATERIAL SELECTION DETAILS' }
  },
  'DomePVPlusPatchPS': {
    shape: { moveAfter: 'shapeType' },
    numberOfColors: { moveToTop: 'MATERIAL SELECTION DETAILS' }
  },

  // Woven Patches
  'WovenPatchHA': {
    ...threadColors,
    shape: { moveAfter: 'shapeType' }
  },
  'WovenPatchPS': {
    ...threadColors,
    shape: { moveAfter: 'shapeType' }
  },

  // UltraColor® MAX (UltraColorDTFTransfers)
  'DTFTransfer': {
    media: { hide: true },
    mediaText: { hide: true },
    mediaType: { hide: true }
  },

  // Custom Patches 
  'CustomPatchesEmbroidered': {
    productType: { moveAfter: 'serviceType' },
    adhesiveType: { moveAfter: 'serviceType' }
  },
  'CustomPatchesVintage': {
    productType: { moveAfter: 'serviceType' }
  },
  'CustomPatchesWoven': {
    productType: { moveAfter: 'serviceType' }
  },

  // Custom Numbers
  'CustomCutNumbers': {
    sewFile: { moveAfter: 'productType' },
    quoteQty: { hide: true }
  },

  // Custom Letters
  'CustomCutLetters': {
    sewFile: { moveAfter: 'productType' },
    quoteQty: { hide: true }
  },

  // Digital Transfers/Logos
  'DigitalTransfersLogos': {
    mediaColor: { hide: true },
    mediaCode: { hide: true },
    mediaText: { hide: true },
    quoteQty: { hide: true }
  },

  // Backlit Graphics
  'BacklitGraphics': {
    mediaColor: { hide: true },
    mediaText: { hide: true },
    quoteQty: { hide: true }
  },

  // Banners
  'Banners': {
    mediaText: { hide: true },
    quoteQty: { hide: true }
  },

  // Canvas Prints
  'CanvasPrints': {
    mediaColor: { hide: true },
    mediaCode: { hide: true },
    mediaText: { hide: true },
    quoteQty: { hide: true }
  },

  // Dry Erase Graphics
  'DryEraseGraphics': {
    mediaCode: { hide: true },
    mediaText: { hide: true },
    quoteQty: { hide: true }
  },

  // Floor Graphics
  'FloorGraphics': {
    mediaColor: { hide: true },
    mediaCode: { hide: true },
    mediaText: { hide: true },
    quoteQty: { hide: true }
  },

  // Static Clings
  'StaticClings': {
    mediaCode: { hide: true },
    mediaText: { hide: true },
    quoteQty: { hide: true }
  },

  // Posters
  'Posters': {
    mediaColor: { hide: true },
    mediaCode: { hide: true },
    mediaText: { hide: true },
    quoteQty: { hide: true }
  },

  // Helmet Stickers
  'HelmetStickers': {
    media: { hide: true },
    mediaColor: { hide: true },
    mediaCode: { hide: true },
    mediaText: { hide: true },
    quoteQty: { hide: true }
  },

  // Stickers/Decals
  'StickersDecals': {
    media: { hide: true },
    mediaColor: { hide: true },
    mediaCode: { hide: true },
    mediaText: { hide: true },
    quoteQty: { hide: true }
  },

  // WallGraphics
  'WallGraphics': {
    mediaColor: { hide: true },
    mediaCode: { hide: true },
    mediaText: { hide: true },
    quoteQty: { hide: true }
  },

  // CAD-CUT® Designs
  'CustomCadCut': {
    quoteQty: { hide: true }
  },

  // "Kiss Cut® Designs",
  'CustomKissCut': {
    quoteQty: { hide: true }
  },

  // "Pre-Aligned Perma-Twill® Designs"
  'CustomPreAlignedPTW': {
    quoteQty: { hide: true }
  },

  // Standar Designs
  'CustomCutStandard': {
    quoteQty: { hide: true }
  },

  // Custom SimStitch
  'CustomSimStitch': {
    quoteQty: { hide: true }
  },

  // Custom Cut Sign Vinyl
  'CustomCutSignVinyl': {
    quoteQty: { hide: true }
  },

  // Custom Product
  'CustomProduct': {
    requestedFabricText: { moveBefore: ['requestedWidth','requestedHeight', ] },
    requestedHeight: { moveAfter: 'requestedWidth' },
    requestedFabric: { hide: true }
  }
};